import { getDocumentRawBlob } from "@/features/clipboard/api/clipboard";
import { HtmlRenderer } from "@/features/clipboard/components/HtmlRenderer";
import {
  BROWSER_TEXT_EXTENSIONS,
  fileExtension,
  isBrowserImage,
  isHtmlFile,
} from "@/features/clipboard/utils/fileTypes";
import { sanitizeImageBlobForPreview } from "@/utils/sanitize";
import { Download } from "lucide-react";
import { useEffect, useState } from "react";

type HtmlMode = "rendered" | "source";

const HtmlModeToggle = ({
  mode,
  onModeChange,
}: {
  mode: HtmlMode;
  onModeChange: (mode: HtmlMode) => void;
}) => {
  const base = "rounded px-2 py-0.5 text-xs font-medium transition-colors";
  return (
    <div
      role="radiogroup"
      aria-label="HTML display mode"
      className="inline-flex items-center gap-0.5 rounded-[3px] border border-zinc-200 bg-zinc-50 p-0.5"
    >
      <button
        type="button"
        role="radio"
        aria-checked={mode === "rendered"}
        onClick={() => onModeChange("rendered")}
        className={`${base} ${mode === "rendered" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}
        title="Render the HTML as a page"
      >
        Rendered
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={mode === "source"}
        onClick={() => onModeChange("source")}
        className={`${base} ${mode === "source" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}
        title="Show the HTML source"
      >
        Source
      </button>
    </div>
  );
};

/**
 * Shows the user's actual uploaded file: PDFs and images render inline, HTML
 * gets a Rendered/Source toggle, other text shows as-is, and Office formats
 * (pptx/docx/…) that can't render natively in the browser get a download
 * offer. Fetched via the authenticated download route and turned into a
 * same-origin blob: URL so no signed URL / CORS is needed.
 */
export const RawFileRenderer = ({
  docId,
  filename,
}: {
  docId: string;
  filename: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [htmlMode, setHtmlMode] = useState<HtmlMode>("rendered");
  const ext = fileExtension(filename);
  const isHtml = isHtmlFile(filename);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    setState("loading");
    setUrl(null);
    setText(null);
    getDocumentRawBlob(docId)
      .then(async (blob) => {
        if (cancelled) return;
        if (BROWSER_TEXT_EXTENSIONS.has(ext)) {
          const content = await blob.text();
          if (cancelled) return;
          setText(content);
          setState("ready");
          return;
        }
        const previewBlob = await sanitizeImageBlobForPreview(blob, filename);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(previewBlob);
        setUrl(objectUrl);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId, ext, filename]);

  if (state === "loading") {
    return <div className="text-sm text-zinc-400">Loading original&hellip;</div>;
  }
  if (state === "error" || (!url && text == null)) {
    return (
      <div className="text-sm text-zinc-400">
        The original file isn&rsquo;t available yet. If you just uploaded it, it&rsquo;s
        still being stored &mdash; try again shortly.
      </div>
    );
  }

  if (text != null) {
    if (isHtml) {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-zinc-400">Original HTML file</p>
            <HtmlModeToggle mode={htmlMode} onModeChange={setHtmlMode} />
          </div>
          {htmlMode === "rendered" ? (
            <HtmlRenderer content={text} />
          ) : (
            <pre className="overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-zinc-700">
              {text}
            </pre>
          )}
        </div>
      );
    }
    return (
      <pre className="overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-zinc-700">
        {text}
      </pre>
    );
  }

  if (!url) {
    return <div className="text-sm text-zinc-400">The original file could not be loaded.</div>;
  }

  if (ext === "pdf") {
    return (
      <iframe
        src={url}
        className="w-full border-0 bg-white"
        style={{ height: "calc(100vh - 8rem)", minHeight: "500px" }}
        title={`${filename} (original)`}
      />
    );
  }
  if (isBrowserImage(filename)) {
    return (
      <div className="flex justify-center p-2">
        <img src={url} alt={filename} className="max-w-full h-auto rounded" />
      </div>
    );
  }

  // Non-renderable (pptx/docx/xlsx/…): offer the original for download.
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm text-zinc-500">
        <span className="font-mono text-zinc-700">.{ext || "file"}</span> files can&rsquo;t be
        shown in the browser. Download the original to open it.
      </p>
      <a
        href={url}
        download={filename}
        className="inline-flex items-center gap-2 rounded-[3px] border border-zinc-300 bg-white px-3.5 py-1 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
      >
        <Download className="h-4 w-4" />
        Download {filename}
      </a>
    </div>
  );
};
